/**
 * Shared roster dialogs: type-to-confirm (revoke), expiry date, owner metadata. Used by the
 * lookup drawer and the roster table so both write through the same audited endpoints.
 */
import { useState, type ReactNode } from 'react';
import { Button } from './Button';
import { Modal } from './Modal';
import { TextArea, TextInput } from './Field';
import { isApiError } from '@/lib/api';
import { dayToUtcMs, utcDay } from '@/lib/dates';

export const errMsg = (e: unknown): string =>
  isApiError(e) ? e.message : e instanceof Error ? e.message : String(e);

/** The operator retypes `expect` before the destructive action enables. */
export function TypeConfirmModal({
  open, title, body, expect, confirmLabel, reasonLabel, onCancel, onConfirm,
}: {
  open: boolean; title: string; body: ReactNode; expect: string; confirmLabel: string;
  reasonLabel?: string; onCancel: () => void; onConfirm: (reason: string) => Promise<void>;
}) {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const reset = () => { setTyped(''); setReason(''); setErr(null); setBusy(false); };
  const cancel = () => { reset(); onCancel(); };
  const matched = typed.trim() === expect;
  const submit = async () => {
    if (!matched) return;
    setBusy(true); setErr(null);
    try { await onConfirm(reason.trim()); reset(); }
    catch (e) { setErr(errMsg(e)); setBusy(false); }
  };
  return (
    <Modal
      open={open}
      onClose={cancel}
      title={title}
      footer={<><Button onClick={cancel}>取消</Button><Button variant="danger" loading={busy} disabled={!matched} onClick={() => void submit()}>{confirmLabel}</Button></>}
    >
      <div className="stack" style={{ gap: 'var(--space-4)' }}>
        <p className="subtle" style={{ margin: 0 }}>{body}</p>
        <TextInput label={`輸入 ${expect} 以確認`} value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={expect} autoComplete="off" spellCheck={false} />
        {reasonLabel && <TextArea label={reasonLabel} value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />}
        {err && <div className="field-msg field-msg-error" role="alert">{err}</div>}
      </div>
    </Modal>
  );
}

/** Set or clear one machine's expiry (calendar day → UTC midnight epoch ms). */
export function ExpiryModal({
  open, sn, current, onCancel, onSave,
}: {
  open: boolean; sn: string; current: number | null; onCancel: () => void;
  onSave: (expiresAt: number | null) => Promise<void>;
}) {
  const [day, setDay] = useState(current ? utcDay(current) : '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const save = async (value: number | null) => {
    setBusy(true); setErr(null);
    try { await onSave(value); }
    catch (e) { setErr(errMsg(e)); }
    finally { setBusy(false); }
  };
  const ms = dayToUtcMs(day);
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title="設定到期日"
      footer={
        <>
          {current !== null && <Button variant="ghost" disabled={busy} onClick={() => void save(null)}>清除到期日</Button>}
          <span style={{ flex: 1 }} />
          <Button onClick={onCancel}>取消</Button>
          <Button variant="primary" loading={busy} disabled={ms === null} onClick={() => void save(ms)}>儲存</Button>
        </>
      }
    >
      <p className="subtle" style={{ marginTop: 0 }}><span className="mono">{sn}</span> 在到期日之後無法通過准入(以 UTC 日期計)。</p>
      <TextInput label="到期日" type="date" value={day} onChange={(e) => setDay(e.target.value)} />
      {err && <div className="field-msg field-msg-error" role="alert">{err}</div>}
    </Modal>
  );
}

export interface MetaForm { owner_name: string; owner_email: string; team: string; notes: string }

/** Edit owner / email / team / notes. An emptied field is sent as '' (= clear). */
export function MetaModal({
  open, sn, initial, onCancel, onSave,
}: {
  open: boolean; sn: string; initial: MetaForm; onCancel: () => void;
  onSave: (patch: MetaForm) => Promise<void>;
}) {
  const [f, setF] = useState<MetaForm>(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await onSave({ owner_name: f.owner_name.trim(), owner_email: f.owner_email.trim(), team: f.team.trim(), notes: f.notes.trim() });
    } catch (e) { setErr(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={`編輯擁有人 · ${sn}`}
      footer={<><Button onClick={onCancel}>取消</Button><Button variant="primary" loading={busy} onClick={() => void save()}>儲存</Button></>}
    >
      <TextInput label="擁有人" value={f.owner_name} maxLength={200} onChange={(e) => setF({ ...f, owner_name: e.target.value })} />
      <TextInput label="Email" type="email" value={f.owner_email} maxLength={200} onChange={(e) => setF({ ...f, owner_email: e.target.value })} />
      <TextInput label="團隊" value={f.team} maxLength={200} onChange={(e) => setF({ ...f, team: e.target.value })} />
      <TextArea label="備註" value={f.notes} maxLength={1000} rows={2} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      {err && <div className="field-msg field-msg-error" role="alert">{err}</div>}
    </Modal>
  );
}
